import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getRegistroCorrispettivi, recalculateRegistroCorrispettivi, exportRegistroCorrispettiviExcel } from '../../services/api';
import { Table, Form, Spinner, Alert, Row, Col, Pagination, Card, Button, Badge, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../../common/MobileListLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter, faFileExcel, faCalendarAlt, faEuroSign, faChartLine,
  faSyncAlt, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, createColumnHelper } from '@tanstack/react-table';

const columnHelper = createColumnHelper();

const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const formatCurrency = (v) => `€ ${parseFloat(v || 0).toFixed(2)}`;

function CorrispettivoDetailModal({ corrispettivo, onClose }) {
  const dettagliIva = corrispettivo.dettagli_iva || {};
  
  return (
    <Modal show onHide={onClose} size="md">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />
          Dettaglio Corrispettivi - {formatDate(corrispettivo.data)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col><small className="text-muted d-block">Data</small><strong>{formatDate(corrispettivo.data)}</strong></Col>
          <Col><small className="text-muted d-block">Totale Giornaliero</small><span style={{fontSize:'1.2rem',color:'#198754',fontWeight:'bold'}}>{formatCurrency(corrispettivo.totale_giornaliero)}</span></Col>
        </Row>
        {Object.keys(dettagliIva).length > 0 && (
          <>
            <h6 className="mt-3 mb-2">Dettaglio per Aliquota IVA</h6>
            <Table size="sm" bordered>
              <thead><tr><th>Aliquota IVA</th><th className="text-end">Totale</th></tr></thead>
              <tbody>
                {Object.entries(dettagliIva).map(([iva, totale]) => (
                  <tr key={iva}>
                    <td><Badge bg="info">{iva}%</Badge></td>
                    <td className="text-end fw-bold">{formatCurrency(totale)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Chiudi</Button>
      </Modal.Footer>
    </Modal>
  );
}

function RegistroCorrispettivi() {
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [corrispettivi, setCorrispettivi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCorrispettivo, setSelectedCorrispettivo] = useState(null);
  const [totalePeriodo, setTotalePeriodo] = useState(0);
  const [ivaAggregate, setIvaAggregate] = useState({});
  const [mediaGiornaliera, setMediaGiornaliera] = useState(0);
  const [sorting, setSorting] = useState([{ id: 'data', desc: true }]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Selezione multipla
  const [selectedIds, setSelectedIds] = useState([]);

  const buildFilters = useCallback(() => {
    let filters = {};
    if (startDate && endDate) {
      filters.data = { start: startDate, end: endDate };
    } else if (startDate) {
      filters.data = { start: startDate, end: startDate };
    }
    return Object.keys(filters).length > 0 ? JSON.stringify(filters) : null;
  }, [startDate, endDate]);

  const fetchCorrispettivi = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
      const response = await getRegistroCorrispettivi((currentPage - 1) * itemsPerPage, itemsPerPage, sortParams, buildFilters());
      setCorrispettivi(response.data.corrispettivi);
      setTotalItems(response.data.total);
      setTotalePeriodo(response.data.totale_periodo || 0);
      setIvaAggregate(response.data.iva_aggregate || {});
      setMediaGiornaliera(response.data.media_giornaliera || 0);
    } catch (err) {
      setError('Errore nel caricamento del registro corrispettivi.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, buildFilters]);

  useEffect(() => {
    fetchCorrispettivi();
  }, [fetchCorrispettivi]);

  const handleFilter = () => {
    setCurrentPage(1);
    fetchCorrispettivi();
  };

  const handleRecalculate = async () => {
    setRecalcLoading(true);
    setError(null);
    try {
      if (!startDate || !endDate) {
        toast.error("Seleziona sia una Data Inizio che una Data Fine per il ricalcolo.");
        return;
      }
      await recalculateRegistroCorrispettivi(startDate, endDate);
      toast.success(`Ricalcolo del Registro Corrispettivi per il periodo ${startDate} - ${endDate} completato.`);
      fetchCorrispettivi();
    } catch (err) {
      let errorMsg = 'Errore durante il ricalcolo del Registro Corrispettivi.';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail.map(e => e.msg).join('; ');
        } else if (typeof err.response.data.detail === 'object') {
          errorMsg = JSON.stringify(err.response.data.detail);
        } else {
          errorMsg = err.response.data.detail;
        }
      }
      setError(errorMsg);
      toast.error(errorMsg);
      console.error(err);
    } finally {
      setRecalcLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await exportRegistroCorrispettiviExcel(buildFilters());
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `registro_corrispettivi_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Errore durante il download Excel');
    }
  };

  const resetFilters = () => {
    setStartDate(''); setEndDate(''); setCurrentPage(1);
  };

  const hasActiveFilters = startDate || endDate;

  // Gestione selezione multipla
  const toggleSelectAll = () => {
    if (selectedIds.length === corrispettivi.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(corrispettivi.map(c => c.id));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkExport = async () => {
    if (selectedIds.length === 0) return;
    try {
      const selectedFilters = { ids: selectedIds };
      const res = await exportRegistroCorrispettiviExcel(JSON.stringify(selectedFilters));
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `corrispettivi_selezionati_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSelectedIds([]);
    } catch {
      toast.error('Errore durante l\'export dei corrispettivi selezionati');
    }
  };

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'select',
      header: () => (
        <Form.Check
          type="checkbox"
          checked={selectedIds.length === corrispettivi.length && corrispettivi.length > 0}
          onChange={toggleSelectAll}
        />
      ),
      cell: info => (
        <Form.Check
          type="checkbox"
          checked={selectedIds.includes(info.row.original.id)}
          onChange={() => toggleSelectRow(info.row.original.id)}
          onClick={e => e.stopPropagation()}
        />
      ),
    }),
    columnHelper.accessor('data', { header: 'Data', cell: info => formatDate(info.getValue()) }),
    columnHelper.accessor('totale_giornaliero', { header: 'Totale Giornaliero', cell: info => formatCurrency(info.getValue()) }),
    columnHelper.accessor('dettagli_iva', {
      header: 'Dettagli IVA',
      cell: info => {
        const dettagli = info.getValue();
        if (!dettagli) return '—';
        return Object.entries(dettagli)
          .map(([iva, totale]) => `IVA ${iva}%: ${formatCurrency(totale)}`)
          .join(' | ');
      }
    }),
    columnHelper.display({
      id: 'azioni',
      header: '',
      cell: info => (
        <Button size="sm" variant="outline-primary"
          onClick={e => { e.stopPropagation(); setSelectedCorrispettivo(info.row.original); }}
          title="Dettaglio">
          <FontAwesomeIcon icon={faInfoCircle} />
        </Button>
      ),
    }),
  ], [selectedIds, corrispettivi]);

  const table = useReactTable({
    data: corrispettivi,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
  });

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const renderPaginationItems = () => {
    if (totalPages <= 1) return null;
    const items = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    for (let i = start; i <= end; i++) {
      items.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return items;
  };

  const renderCard = useCallback((corr) => {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1"><FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-muted" />{formatDate(corr.data)}</h6>
          </div>
          <Badge bg="success">{formatCurrency(corr.totale_giornaliero)}</Badge>
        </div>
        <Row className="mb-2">
          <Col xs={12}>
            <small className="text-muted d-block">Dettagli IVA</small>
            {corr.dettagli_iva && Object.keys(corr.dettagli_iva).length > 0 ? (
              Object.entries(corr.dettagli_iva).map(([iva, tot]) => (
                <Badge key={iva} bg="info" className="me-1">IVA {iva}%: {formatCurrency(tot)}</Badge>
              ))
            ) : '—'}
          </Col>
        </Row>
        <div className="mt-2">
          <Button size="sm" variant="outline-primary" onClick={() => setSelectedCorrispettivo(corr)}>Dettaglio</Button>
        </div>
      </div>
    );
  }, []);

  return (
    <>
      {selectedCorrispettivo && (
        <CorrispettivoDetailModal
          corrispettivo={selectedCorrispettivo}
          onClose={() => setSelectedCorrispettivo(null)}
        />
      )}
      <Row className="mb-3 align-items-center">
        <Col><h1 className="h3 mb-0">Registro Corrispettivi</h1></Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => setShowFilters(p => !p)}>
            <FontAwesomeIcon icon={faFilter} className="me-1" />
            Filtri {hasActiveFilters && <Badge bg="danger" className="ms-1">{[startDate, endDate].filter(Boolean).length}</Badge>}
          </Button>
          <Button variant="outline-success" onClick={handleExportExcel}>
            <FontAwesomeIcon icon={faFileExcel} className="me-1" />Excel
          </Button>
        </Col>
      </Row>
      {/* KPI */}
      <Row className="mb-3">
        <Col md={4}>
          <Card className="border-success">
            <Card.Body className="py-2">
              <small className="text-muted d-block">Totale periodo</small>
              <span className="fs-5 fw-bold text-success"><FontAwesomeIcon icon={faEuroSign} className="me-1" />{formatCurrency(totalePeriodo)}</span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-primary">
            <Card.Body className="py-2">
              <small className="text-muted d-block">Media giornaliera</small>
              <span className="fs-5 fw-bold text-primary"><FontAwesomeIcon icon={faChartLine} className="me-1" />{formatCurrency(mediaGiornaliera)}</span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-info">
            <Card.Body className="py-2">
              <small className="text-muted d-block">Totale IVA periodo</small>
              <div className="mt-1">
                {Object.keys(ivaAggregate).length > 0 ? (
                  Object.entries(ivaAggregate).map(([iva, tot]) => (
                    <Badge key={iva} bg="info" className="me-1">{iva}%: {formatCurrency(tot)}</Badge>
                  ))
                ) : <span className="text-muted">—</span>}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Filtri */}
      {showFilters && (
        <Card className="mb-3 border-secondary">
          <Card.Header className="d-flex justify-content-between align-items-center py-2">
            <span><FontAwesomeIcon icon={faFilter} className="me-2" />Filtri e Ricalcolo</span>
            <Button size="sm" variant="outline-secondary" onClick={resetFilters}>Azzera filtri</Button>
          </Card.Header>
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col md={3}>
                <Form.Label className="small mb-1">Data Inizio</Form.Label>
                <Form.Control size="sm" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </Col>
              <Col md={3}>
                <Form.Label className="small mb-1">Data Fine</Form.Label>
                <Form.Control size="sm" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </Col>
              <Col md={3}>
                <Button size="sm" onClick={handleFilter} className="w-100">Filtra</Button>
              </Col>
              <Col md={3}>
                <Button size="sm" variant="danger" onClick={handleRecalculate} className="w-100" disabled={recalcLoading || loading}>
                  {recalcLoading ? <><Spinner as="span" animation="border" size="sm" /> Ricalcolo...</> : <><FontAwesomeIcon icon={faSyncAlt} className="me-1" />Ricalcola</>}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Barra azioni bulk */}
      {selectedIds.length > 0 && !isMobile && (
        <Alert variant="info" className="mb-3 d-flex justify-content-between align-items-center">
          <span>
            <strong>{selectedIds.length}</strong> corrispettivo/i selezionato/i
          </span>
          <div>
            <Button size="sm" variant="success" onClick={handleBulkExport} className="me-2">
              <FontAwesomeIcon icon={faFileExcel} className="me-1" />
              Esporta selezionati
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setSelectedIds([])}>
              Deseleziona tutto
            </Button>
          </div>
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0 p-md-3">
          {(loading || recalcLoading) && <div className="text-center py-4"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {!loading && !recalcLoading && !error && (
            isMobile ? (
              <MobileListLayout
                items={corrispettivi}
                renderCard={renderCard}
                entityName="corrispettivo"
                showCreateButton={false}
                showSelectAll={false}
              />
            ) : (
              <Table striped bordered hover responsive size="sm">
                <thead>
                  {table.getHeaderGroups().map(hg => (
                    <tr key={hg.id}>
                      {hg.headers.map(header => (
                        <th key={header.id}
                          style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                          onClick={header.column.getToggleSortingHandler()}>
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted()] ?? null}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr><td colSpan={5} className="text-center text-muted py-4">Nessun corrispettivo trovato</td></tr>
                  ) : (
                    table.getRowModel().rows.map(row => (
                      <tr key={row.id} style={{ cursor: 'pointer' }} onDoubleClick={() => setSelectedCorrispettivo(row.original)}>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )
          )}
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Form.Group as={Row} className="align-items-center mb-0">
            <Col xs="auto"><Form.Label className="mb-0 small">Righe</Form.Label></Col>
            <Col xs="auto">
              <Form.Select size="sm" value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
              </Form.Select>
            </Col>
            <Col xs="auto"><small className="text-muted">Totale: {totalItems}</small></Col>
          </Form.Group>
          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
            {renderPaginationItems()}
            <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages} />
            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages} />
          </Pagination>
        </Card.Footer>
      </Card>
    </>
  );
}

export default RegistroCorrispettivi;
